'use client';

import * as React from 'react';
import { TextIcon } from '@/components/atoms/TextIcon';
import { cn } from '@/components/atoms/primitives/utils';
import { useAuth } from '@/contexts/AuthContext';
import { crossHostLogin, crossHostSignup } from '@/lib/cross-host-redirect';

/* ------------------------------------------------------------------------- */
/*  Public types                                                             */
/* ------------------------------------------------------------------------- */

export type FriendTag = 'CLOSE' | 'RECENT' | 'MUTUAL';

export interface SocialDualStateFriend {
  /** 1–2 character avatar initials (e.g. "JK"). */
  initials: string;
  /** Friend display name (e.g. "Jamie Kessler"). */
  name: string;
  /**
   * Subtitle — relationship context the API genuinely supplies (e.g. mutual
   * count). Omitted when no real value exists; the row simply renders without
   * a subtitle rather than fabricating one.
   */
  subtitle?: string;
  /**
   * Relationship tag shown on the right of each list row. Only present when
   * the API genuinely classifies the relationship. Omitted otherwise (no
   * position-derived fabrication).
   */
  tag?: FriendTag;
}

/** Crowd insight cell — age range + locality label, when the API provides both. */
export interface SocialDualStateCrowdInsight {
  ageRange: string;
  localityLabel: string;
}

/** Shape of the social-proof data needed to render the auth branch. */
export interface SocialDualStateAuthData {
  /**
   * Friends RSVPing — the avatar stack samples the first 4 and overflows the
   * rest. Empty at launch (no friend graph yet); the empty state is rendered
   * honestly rather than inventing people.
   */
  friendsGoing: SocialDualStateFriend[];
  /**
   * Personalisation lift — "Closer to your taste than N% of CLT shows".
   * `null` when the API has no score for this user; the line is suppressed.
   */
  tasteFitPercent: number | null;
  /** Crowd insight cell. `null` when the API has no stable signal yet. */
  crowdInsight: SocialDualStateCrowdInsight | null;
}

/* ------------------------------------------------------------------------- */
/*  View — discriminated union                                               */
/* ------------------------------------------------------------------------- */

export type SocialDualStateCardViewProps =
  | (SocialDualStateAuthData & {
      state: 'auth';
      /** Total LOCI users RSVPing (friends + non-friends). */
      totalGoingCount: number;
      /** Callback when the primary RSVP button is clicked. */
      onRsvpToggle: () => void;
      /** Toggles the RSVP button's label and `aria-pressed`. */
      isRsvpd: boolean;
      /**
       * In-flight RSVP mutation. Disables the button and exposes `aria-busy`
       * so repeat taps can't queue duplicate mutations. Defaults to `false`.
       */
      isRsvpPending?: boolean;
      className?: string;
    })
  | {
      state: 'anon';
      /** Total LOCI users RSVPing — interpolated into the teaser copy. */
      totalGoingCount: number;
      /** Absolute URL to the signup surface (built via `crossHostSignup`). */
      signupHref: string;
      /** Absolute URL to the login surface (built via `crossHostLogin`). */
      loginHref: string;
      className?: string;
    };

/* ------------------------------------------------------------------------- */
/*  Visual constants                                                         */
/* ------------------------------------------------------------------------- */

/** Maximum number of avatars shown in the stack before overflow. */
const AVATAR_STACK_LIMIT = 4;
/** Placeholder dots in the anon teaser stack. */
const ANON_PLACEHOLDER_COUNT = 4;

const AVATAR_BASE_CLASS =
  'inline-flex items-center justify-center h-7 w-7 rounded-full bg-clt-skyline-wash border border-clt-skyline text-micro font-bold tracking-[0.04em] text-clt-skyline-glow-fg -ml-2 first:ml-0';

const TAG_BASE_CLASS =
  'inline-flex items-center rounded-ds-xs px-2 py-[2px] text-micro font-bold uppercase tracking-[0.06em]';

const TAG_NEUTRAL_CLASS = 'bg-clt-skyline-wash text-clt-skyline-glow-fg border border-clt-skyline';

const TAG_RECENT_CLASS = 'bg-clt-ember-wash text-clt-ember-fg border border-clt-ember';

function tagClassNameFor(tag: FriendTag): string {
  // CLOSE and MUTUAL share the neutral palette; RECENT carries the ember accent.
  return cn(TAG_BASE_CLASS, tag === 'RECENT' ? TAG_RECENT_CLASS : TAG_NEUTRAL_CLASS);
}

/* ------------------------------------------------------------------------- */
/*  View                                                                     */
/* ------------------------------------------------------------------------- */

/**
 * SocialDualStateCardView — pure presentational dual-state social card.
 *
 * Renders the **auth** branch (friends list with an honest empty state,
 * taste-fit lift and crowd insight when the API provides them, RSVP) when
 * `props.state === 'auth'`, and the **anon** branch
 * (teaser stack, signup CTA, login link) when `props.state === 'anon'`.
 *
 * Used directly by Storybook and unit tests. The connected wrapper
 * (`SocialDualStateCard`) decides which state to pass based on `useAuth()`.
 */
export function SocialDualStateCardView(props: SocialDualStateCardViewProps): React.ReactElement {
  const { state, className } = props;
  // Unique id per instance — prevents duplicate-id violations when the
  // organism is rendered twice on the same page (e.g. layout previews,
  // server + client islands, story compositions).
  const titleId = `${React.useId()}-social-dual-state-card-title`;

  return (
    <section
      data-testid="social-dual-state-card"
      data-state={state}
      aria-labelledby={titleId}
      className={cn(
        'flex flex-col gap-3',
        'rounded-ds-md border border-clt-skyline',
        'bg-clt-skyline-wash',
        'p-4',
        'text-fg-1',
        className,
      )}
    >
      <header className="flex items-baseline justify-between gap-2">
        <h3
          id={titleId}
          className="font-display text-small font-bold tracking-[0.02em] text-clt-skyline-glow-fg"
        >
          Who&apos;s going <small className="font-medium text-fg-2">· FOR YOU</small>
        </h3>
      </header>

      {state === 'auth' ? <AuthBranch {...props} /> : <AnonBranch {...props} />}
    </section>
  );
}

/* ------------------------------------------------------------------------- */
/*  Auth branch                                                              */
/* ------------------------------------------------------------------------- */

interface AuthBranchProps extends SocialDualStateAuthData {
  totalGoingCount: number;
  onRsvpToggle: () => void;
  isRsvpd: boolean;
  isRsvpPending?: boolean;
}

function AuthBranch({
  friendsGoing,
  totalGoingCount,
  tasteFitPercent,
  crowdInsight,
  onRsvpToggle,
  isRsvpd,
  isRsvpPending = false,
}: AuthBranchProps): React.ReactElement {
  const visibleAvatars = friendsGoing.slice(0, AVATAR_STACK_LIMIT);
  const hiddenCount = Math.max(friendsGoing.length - AVATAR_STACK_LIMIT, 0);
  const hasFriends = friendsGoing.length > 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        {hasFriends ? (
          <div
            data-testid="social-dual-state-card-avatar-stack"
            className="flex"
            aria-hidden="true"
          >
            {visibleAvatars.map((friend, idx) => (
              <span
                key={`${friend.initials}-${idx}`}
                className={AVATAR_BASE_CLASS}
                data-testid="social-dual-state-card-avatar"
              >
                {friend.initials}
              </span>
            ))}
            {hiddenCount > 0 ? (
              <span
                className={AVATAR_BASE_CLASS}
                data-testid="social-dual-state-card-avatar-overflow"
              >
                {`+${hiddenCount}`}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="flex flex-col gap-[2px] text-small leading-[1.4]">
          <span data-testid="social-dual-state-card-crowd-summary">
            {hasFriends ? (
              <>
                <b className="text-fg-1">
                  {friendsGoing.length} {friendsGoing.length === 1 ? 'friend' : 'friends'}
                </b>{' '}
                going · {totalGoingCount} LOCI total
              </>
            ) : (
              <>
                <b className="text-fg-1">{totalGoingCount} LOCI total</b> going
              </>
            )}
          </span>
          {tasteFitPercent !== null ? (
            <small data-testid="social-dual-state-card-taste-fit" className="text-micro text-fg-2">
              {`Closer to your taste than ${tasteFitPercent}% of CLT shows`}
            </small>
          ) : null}
        </div>
      </div>

      {hasFriends ? (
        <ul data-testid="social-dual-state-card-friends-list" className="flex flex-col gap-2">
          {friendsGoing.slice(0, 3).map((friend, idx) => (
            <li key={`${friend.initials}-row-${idx}`} className="flex items-center gap-2">
              <span className={AVATAR_BASE_CLASS} aria-hidden="true">
                {friend.initials}
              </span>
              <span className="flex flex-1 flex-col text-caption leading-[1.35]">
                <span className="text-fg-1">{friend.name}</span>
                {friend.subtitle ? (
                  <small className="text-micro uppercase tracking-[0.06em] text-fg-2">
                    {friend.subtitle}
                  </small>
                ) : null}
              </span>
              {friend.tag ? (
                <span
                  data-testid="social-dual-state-card-friend-tag"
                  data-tag={friend.tag}
                  className={tagClassNameFor(friend.tag)}
                >
                  {friend.tag}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p
          data-testid="social-dual-state-card-empty-friends"
          className="text-caption leading-[1.45] text-fg-2"
        >
          None of your connections have RSVPed yet. Be the first to mark that you&apos;re going.
        </p>
      )}

      {crowdInsight ? (
        <div className="rounded-ds-sm border border-clt-skyline bg-clt-skyline-wash p-2">
          <div data-testid="social-dual-state-card-insight-crowd" className="flex flex-col">
            <span className="text-micro uppercase tracking-[0.06em] text-fg-2">Crowd</span>
            <span className="text-caption font-semibold text-fg-1">
              {`${crowdInsight.localityLabel} · ${crowdInsight.ageRange}`}
            </span>
          </div>
        </div>
      ) : null}

      <div className="flex gap-2">
        <button
          type="button"
          data-testid="social-dual-state-card-rsvp"
          aria-pressed={isRsvpd}
          aria-busy={isRsvpPending}
          disabled={isRsvpPending}
          onClick={onRsvpToggle}
          className={cn(
            'inline-flex min-h-[44px] desktop:min-h-0 flex-1 items-center justify-center rounded-ds-sm px-3 text-small font-bold transition-colors',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clt-skyline-glow',
            'motion-reduce:transition-none',
            'disabled:cursor-not-allowed disabled:opacity-60',
            isRsvpd
              ? 'bg-clt-skyline-glow text-n-900'
              : 'bg-clt-skyline text-fg-1 hover:bg-clt-skyline-glow hover:text-n-900',
          )}
        >
          {isRsvpd ? 'Going ✓' : "I'm going"}
        </button>
        <button
          type="button"
          aria-label="More RSVP options"
          className="inline-flex min-h-[44px] desktop:min-h-0 min-w-[44px] desktop:min-w-0 items-center justify-center rounded-ds-sm border border-clt-skyline bg-transparent px-2 text-fg-2 hover:text-fg-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clt-skyline-glow motion-reduce:transition-none"
        >
          ···
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/*  Anon branch                                                              */
/* ------------------------------------------------------------------------- */

interface AnonBranchProps {
  totalGoingCount: number;
  signupHref: string;
  loginHref: string;
}

function AnonBranch({
  totalGoingCount,
  signupHref,
  loginHref,
}: AnonBranchProps): React.ReactElement {
  const anonOverflow = Math.max(totalGoingCount - ANON_PLACEHOLDER_COUNT, 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div data-testid="social-dual-state-card-anon-stack" className="flex" aria-hidden="true">
          {Array.from({ length: ANON_PLACEHOLDER_COUNT }).map((_, idx) => (
            <span
              key={`placeholder-${idx}`}
              className={AVATAR_BASE_CLASS}
              data-testid="social-dual-state-card-anon-placeholder"
            >
              ·
            </span>
          ))}
          {anonOverflow > 0 ? (
            <span className={AVATAR_BASE_CLASS} data-testid="social-dual-state-card-anon-overflow">
              {`+${anonOverflow}`}
            </span>
          ) : null}
        </div>
        <div
          data-testid="social-dual-state-card-anon-tease"
          className="text-small leading-[1.4] text-fg-1"
        >
          <b>{`${totalGoingCount} LOCI users`}</b> are going to this show.
        </div>
      </div>

      <p className="text-micro leading-[1.45] text-fg-2">
        See who&apos;s in your circle, get crowd-fit signals, and RSVP with one tap. We never share
        your name without your say-so.
      </p>

      <div className="flex flex-col gap-2">
        <a
          data-testid="social-dual-state-card-signup"
          href={signupHref}
          className={cn(
            'inline-flex min-h-[44px] desktop:min-h-0 items-center justify-center rounded-ds-sm px-3 text-small font-bold no-underline',
            // WEB-459: `text-[...]!` (Tailwind v4 important suffix) — this is an
            // <a>, and the then-unlayered base `a { color: inherit }` in
            // globals.css (layered in styles/base.css since WEB-853) beat the
            // layered colour utility, so the label would inherit the
            // body text (white in dark) on the bright glow button → 2.1:1.
            // `!important` forces the dark navy on-glow colour, which clears AA
            // in both themes.
            'bg-clt-skyline-glow text-n-900!',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clt-skyline-glow',
            'motion-reduce:transition-none',
          )}
        >
          Create your LOCI <TextIcon />
        </a>
        <a
          data-testid="social-dual-state-card-login"
          href={loginHref}
          className={cn(
            'inline-flex min-h-[44px] desktop:min-h-0 items-center justify-center rounded-ds-sm px-3 text-caption font-medium',
            'text-fg-2 hover:text-accent-text',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clt-skyline-glow',
            'no-underline',
            'motion-reduce:transition-none',
          )}
        >
          Already a member? <b className="ml-1">Sign in</b>
        </a>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/*  Connected                                                                */
/* ------------------------------------------------------------------------- */

export interface SocialDualStateCardProps {
  /**
   * Same-origin path of the event being viewed — embedded into the cross-host
   * signup / login URLs so the user lands back on this page after auth.
   */
  eventPath: string;
  /** Total LOCI users RSVPing for this event — shown in both branches. */
  totalGoingCount: number;
  /**
   * Auth-only social data. SSR / unauthenticated requests pass `null`, which
   * forces the anon branch. The auth branch is suppressed until the data is
   * available and the user is verified authenticated client-side.
   */
  socialData: SocialDualStateAuthData | null;
  /** RSVP state for the current user (used only on the auth branch). */
  isRsvpd: boolean;
  /** RSVP toggle callback (used only on the auth branch). */
  onRsvpToggle: () => void;
  /** In-flight RSVP mutation — disables the button (auth branch only). */
  isRsvpPending?: boolean;
  /** Pass-through CSS class for the outer card. */
  className?: string;
}

/**
 * SocialDualStateCard — connected wrapper. Renders the anon branch by default
 * (SSR + unauthenticated visitors) and swaps to the auth branch only when
 * `useAuth()` has finished loading, reports the user as authenticated, and
 * upstream social data is available.
 */
export function SocialDualStateCard({
  eventPath,
  totalGoingCount,
  socialData,
  isRsvpd,
  onRsvpToggle,
  isRsvpPending = false,
  className,
}: SocialDualStateCardProps): React.ReactElement {
  const { isAuthenticated, isLoading } = useAuth();

  const shouldRenderAuth = isAuthenticated && !isLoading && socialData !== null;

  if (shouldRenderAuth) {
    return (
      <SocialDualStateCardView
        state="auth"
        friendsGoing={socialData.friendsGoing}
        totalGoingCount={totalGoingCount}
        tasteFitPercent={socialData.tasteFitPercent}
        crowdInsight={socialData.crowdInsight}
        isRsvpd={isRsvpd}
        onRsvpToggle={onRsvpToggle}
        isRsvpPending={isRsvpPending}
        className={className}
      />
    );
  }

  return (
    <SocialDualStateCardView
      state="anon"
      totalGoingCount={totalGoingCount}
      signupHref={crossHostSignup(eventPath)}
      loginHref={crossHostLogin(eventPath)}
      className={className}
    />
  );
}
